import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, PencilSimple } from '@phosphor-icons/react';
import ImageGallery from './ImageGallery';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import NotificationAlert from '../../../shared/components/ui/NotificationAlert';
import Cookies from 'js-cookie';
import { jwtDecode } from "jwt-decode";
import * as technicianService from '../../../services/technicianService';

const IMAGE_UPLOAD_LIMIT_STATUS = 406;

const getPartialUploadNotification = (uploadSummary, backendMessage, reachedImageLimit) => {
    const rejectedFiles = Object.values(uploadSummary.remainingFilesByField || {}).flat();
    const rejectedNames = rejectedFiles.map(file => file.name).join(', ');
    const uploadedCount = uploadSummary.uploadedCount || 0;
    const rejectedCount = rejectedFiles.length;
    const fallbackMessage = reachedImageLimit
        ? 'Se alcanzó el límite de imágenes permitido.'
        : 'La carga de imágenes no pudo completarse.';
    const limitMessage = backendMessage?.trim() || fallbackMessage;
    const uploadedMessage = uploadedCount > 0
        ? `Se subieron ${uploadedCount} imagen${uploadedCount === 1 ? '' : 'es'}.`
        : '';
    const rejectedMessage = rejectedCount > 0
        ? `No se subieron ${rejectedCount} imagen${rejectedCount === 1 ? '' : 'es'}: ${rejectedNames}.`
        : '';

    return `${limitMessage}\n${uploadedMessage}${rejectedMessage}`;
};

const revokePendingUploads = (pendingUploadsByField) => {
    Object.values(pendingUploadsByField).forEach(items => {
        items.forEach(item => URL.revokeObjectURL(item.previewUrl));
    });
};

const FormResponseEditModal = ({ isOpen, formResponse, initialMode = 'view', onClose, onSave, onDelete }) => {
    const [editForm, setEditForm] = useState({});
    const [mode, setMode] = useState('view'); // 'view' or 'edit'
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [user, setUser] = useState('')
    // Cantidad de imágenes por campo, reportada por cada ImageGallery (badge "Sin completar" + bloqueo de guardado)
    const [imageCounts, setImageCounts] = useState({});
    const handleImageCountChange = (key) => (count) => {
        setImageCounts(prev => (prev[key] === count ? prev : { ...prev, [key]: count }));
    };

    // Cambios de imágenes diferidos a "Guardar Cambios" (open-questions.md B2 = Opción B):
    // agregar y eliminar no llaman al backend al toque, quedan en cola local hasta guardar.
    const [pendingUploads, setPendingUploads] = useState({}); // { [fieldName]: { id, file, previewUrl }[] }
    const [pendingDeletes, setPendingDeletes] = useState({}); // { [fieldName]: fileId[] }
    const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStage, setSaveStage] = useState(null); // null | 'uploading' | 'deleting' | 'saving-form'
    const [saveProgress, setSaveProgress] = useState(0);
    const [saveError, setSaveError] = useState(null);
    const [uploadNotification, setUploadNotification] = useState(null);
    const modalContentRef = useRef(null);

    useEffect(() => {
        if (uploadNotification?.show) {
            modalContentRef.current?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [uploadNotification]);

    // El aviso de límite permanece visible hasta que se quite de la cola cada
    // imagen rechazada. Así no desaparece antes de que el usuario pueda actuar.
    useEffect(() => {
        const hasPendingUploads = Object.values(pendingUploads).some(items => items.length > 0);

        if (uploadNotification?.show && !hasPendingUploads) {
            setUploadNotification(null);
        }
    }, [pendingUploads, uploadNotification?.show]);

    const handleQueueUpload = (key) => (files) => {
        const items = files.map(file => ({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
            file,
            previewUrl: URL.createObjectURL(file)
        }));
        setPendingUploads(prev => ({ ...prev, [key]: [...(prev[key] || []), ...items] }));
    };

    const handleDequeueUpload = (key) => (item) => {
        URL.revokeObjectURL(item.previewUrl);
        setPendingUploads(prev => ({ ...prev, [key]: (prev[key] || []).filter(i => i.id !== item.id) }));
    };

    const handleMarkDelete = (key) => (fileId) => {
        setPendingDeletes(prev => (
            (prev[key] || []).includes(fileId)
                ? prev
                : { ...prev, [key]: [...(prev[key] || []), fileId] }
        ));
    };

    const handleUnmarkDelete = (key) => (fileId) => {
        setPendingDeletes(prev => ({ ...prev, [key]: (prev[key] || []).filter(id => id !== fileId) }));
    };

    // Actualiza un campo dentro de editForm.formData — reemplaza el spread manual
    // que se repetía en cada input/textarea de renderFieldValue
    const updateFormDataField = (key, value) => {
        setEditForm(prev => ({ ...prev, formData: { ...prev.formData, [key]: value } }));
    };

    // Sincronizar el estado interno cuando cambia formResponse
    useEffect(() => {
        if (formResponse) {
            setEditForm(formResponse);
            setMode(initialMode === 'edit' ? 'edit' : 'view');
            // Descartar cualquier cola de imágenes pendiente de una edición anterior
            setPendingUploads(prev => {
                revokePendingUploads(prev);
                return {};
            });
            setPendingDeletes({});
            setSaveError(null);
            setUploadNotification(null);
        }
        try {
            const token = Cookies.get('sessionToken');
            if (token) {
                const decoded = jwtDecode(token);
                setUser(decoded);
            }
        } catch (error) {
            console.error('Error detecting user role:', error);
        }
    }, [formResponse, initialMode]);

    const toggleMode = () => {
        setMode(mode === 'view' ? 'edit' : 'view');
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete(editForm.id);
        }
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    // "Cancelar" descarta toda la cola de imágenes pendiente sin ningún efecto en backend —
    // mismo criterio que ya aplica hoy a los campos de texto (open-questions.md B2 = Opción B)
    const handleCancel = () => {
        setPendingUploads(prev => {
            revokePendingUploads(prev);
            return {};
        });
        setPendingDeletes({});
        setSaveError(null);
        setUploadNotification(null);
        onClose();
    };

    const keepOnlyRejectedUploads = (uploadSummary) => {
        const remainingFilesByField = uploadSummary?.remainingFilesByField || {};

        setPendingUploads(prev => {
            const nextPendingUploads = {};

            Object.entries(prev).forEach(([fieldName, items]) => {
                const remainingFiles = remainingFilesByField[fieldName] || [];
                const remainingItems = items.filter(item => remainingFiles.includes(item.file));

                items
                    .filter(item => !remainingItems.includes(item))
                    .forEach(item => URL.revokeObjectURL(item.previewUrl));

                if (remainingItems.length > 0) {
                    nextPendingUploads[fieldName] = remainingItems;
                }
            });

            return nextPendingUploads;
        });
    };

    if (!isOpen || !editForm.form) return null;

    // Función para detectar si un valor es una imagen
    const isImageFile = (value) => {
        if (!value || typeof value !== 'string') return false;

        const lowerValue = value.toLowerCase();

        // Verificar extensiones de archivo de imagen
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        if (imageExtensions.some(ext => lowerValue.includes(ext))) {
            return true;
        }

        // Verificar si es una URL de imagen (data URL o http/https)
        if (lowerValue.startsWith('data:image/') ||
            (lowerValue.startsWith('http') && imageExtensions.some(ext => lowerValue.includes(ext)))) {
            return true;
        }

        // Verificar si contiene palabras relacionadas con imágenes
        const imageKeywords = ['foto', 'photo', 'imagen', 'image', 'captura', 'screenshot', 'fotografia'];
        if (imageKeywords.some(keyword => lowerValue.includes(keyword))) {
            return true;
        }

        // Verificar si es un nombre de archivo que parece ser una imagen
        if (lowerValue.includes('img') || lowerValue.includes('pic') || lowerValue.includes('photo')) {
            return true;
        }

        return false;
    };

    // Bloque de ImageGallery + badge "Sin completar", compartido por los dos casos de
    // renderFieldValue que muestran un campo de imagen (array vacío y fieldType image/file)
    const renderImageGalleryField = (key, response, isViewMode) => (
        <div className="bg-surface-2 border border-line rounded-lg p-4">
            {imageCounts[key] === 0 && (
                <span className="inline-block mb-3 text-xs font-bold uppercase tracking-wide text-amber bg-amber/12 px-2 py-0.5 rounded-full">
                    Sin completar
                </span>
            )}
            <ImageGallery
                formId={response.form.id}
                taskId={response.task.id}
                technicianEmail={response.technician.email}
                fieldName={key}
                editable={!isViewMode && user.role === 'admin'}
                onCountChange={handleImageCountChange(key)}
                pendingUploads={pendingUploads[key] || []}
                onQueueUpload={handleQueueUpload(key)}
                onDequeueUpload={handleDequeueUpload(key)}
                pendingDeletes={pendingDeletes[key] || []}
                onMarkDelete={handleMarkDelete(key)}
                onUnmarkDelete={handleUnmarkDelete(key)}
            />
        </div>
    );

    // Función para renderizar el valor de un campo de forma dinámica
    const renderFieldValue = (key, value, formFields = [], response = null, isViewMode = false) => {
        // Buscar la definición del campo en el formulario (por name o id)
        const fieldDefinition = formFields.find(field => field.name === key || field.id === key);
        const fieldType = fieldDefinition?.type || 'text';

        // Si el valor es un array (archivos subidos o campo de imagen)
        if (Array.isArray(value)) {
            // Si el array está vacío Y tenemos response data, mostrar ImageGallery (imágenes en Google Drive)
            if (response && response.form && response.task && response.technician) {
                return (
                    <div className="space-y-4">
                        {renderImageGalleryField(key, response, isViewMode)}
                    </div>
                );
            }
        }

        // Si es un campo de tipo image o file definido en el formulario,
        // usar ImageGallery para mostrar imágenes de Google Drive
        // MEJORADO: También detectar si el nombre del campo sugiere que es una imagen
        const looksLikeImageField = key.toLowerCase().includes('foto') ||
            key.toLowerCase().includes('image') ||
            key.toLowerCase().includes('img') ||
            key.toLowerCase().includes('picture') ||
            key.toLowerCase().includes('acomet');
        if (((fieldType === 'image' || fieldType === 'file') || looksLikeImageField) && response) {
            if (response.form && response.task && response.technician) {
                return (
                    <div className="space-y-4">
                        {renderImageGalleryField(key, response, isViewMode)}
                        {/* Campo de texto para mostrar valor almacenado */}
                        <div>
                            <label className="block text-xs text-content-muted mb-1">Valor almacenado</label>
                            <input
                                type="text"
                                value={Array.isArray(value) ? value.join(', ') : value || ''}
                                disabled
                                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content-muted text-sm disabled:opacity-60"
                            />
                        </div>
                    </div>
                );
            } else {
                console.warn('⚠️ Campo de imagen detectado pero faltan datos:', {
                    hasForm: !!response?.form,
                    hasTask: !!response?.task,
                    hasTechnician: !!response?.technician
                });
            }
        }

        // Si es un archivo individual (string suelto que parece ser un archivo/imagen, sin
        // datos de response para resolverlo vía ImageGallery): no hay forma confiable de
        // mostrarlo, solo se informa que no se encontró la imagen.
        if (typeof value === 'string' && (value.includes('Archivos seleccionados') || isImageFile(value))) {
            return (
                <div className="text-center text-content-muted p-4 border border-line rounded">
                    No se encontraron imágenes para este campo.
                </div>
            );
        }
        // Si es un texto largo, usar textarea
        if (typeof value === 'string' && value.length > 100) {
            return (
                <textarea
                    value={value || ''}
                    onChange={(e) => updateFormDataField(key, e.target.value)}
                    disabled={isViewMode}
                    rows={4}
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60 disabled:cursor-not-allowed"
                    placeholder={`Valor para ${key}...`}
                />
            );
        }

        // Campo de texto normal
        return (
            <input
                type="text"
                value={value || ''}
                onChange={(e) => updateFormDataField(key, e.target.value)}
                disabled={isViewMode}
                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60 disabled:cursor-not-allowed"
                maxLength={255}
                placeholder='Sin respuesta'
            />
        );
    };

    const totalPendingDeletes = Object.values(pendingDeletes).reduce((sum, ids) => sum + ids.length, 0);

    // Orquesta el guardado real de imágenes (open-questions.md B2=B, B3=A): subir pendientes →
    // borrar marcadas → persistir el resto del formulario, secuencial y con corte temprano.
    // Si algún paso falla, se corta ahí y la cola pendiente se conserva para poder reintentar.
    const performSave = async () => {
        setSaveError(null);
        setUploadNotification(null);
        setSaving(true);
        let currentStage = null;
        try {
            const fieldsWithUploads = Object.fromEntries(
                Object.entries(pendingUploads)
                    .filter(([, items]) => items.length > 0)
                    .map(([key, items]) => [key, items.map(item => item.file)])
            );
            if (Object.keys(fieldsWithUploads).length > 0) {
                currentStage = 'uploading';
                setSaveStage(currentStage);
                setSaveProgress(0);
                await technicianService.uploadImages(
                    fieldsWithUploads,
                    { formId: editForm.form.id, taskId: editForm.task.id, technicianEmail: editForm.technician.email },
                    (progress) => setSaveProgress(progress)
                );
            }

            const fieldsWithDeletes = Object.entries(pendingDeletes).filter(([, ids]) => ids.length > 0);
            if (fieldsWithDeletes.length > 0) {
                currentStage = 'deleting';
                setSaveStage(currentStage);
                for (const [, fileIds] of fieldsWithDeletes) {
                    for (const fileId of fileIds) {
                        await technicianService.deleteImage(fileId);
                    }
                }
            }

            currentStage = 'saving-form';
            setSaveStage(currentStage);
            await onSave(editForm);

            revokePendingUploads(pendingUploads);
            setPendingUploads({});
            setPendingDeletes({});
        } catch (err) {
            console.error('Error guardando cambios:', err);
            if (currentStage === 'uploading' && err.uploadSummary) {
                keepOnlyRejectedUploads(err.uploadSummary);
                setUploadNotification({
                    show: true,
                    type: 'error',
                    message: getPartialUploadNotification(
                        err.uploadSummary,
                        err.message,
                        err.status === IMAGE_UPLOAD_LIMIT_STATUS
                    ),
                    origin: 'form-response-edit-modal'
                });
                return;
            }
            const stageMessages = {
                uploading: 'No se pudieron subir las imágenes pendientes. Intenta guardar de nuevo.',
                deleting: 'No se pudieron eliminar las imágenes marcadas. Intenta guardar de nuevo.',
                'saving-form': 'No se pudieron guardar los cambios del formulario. Inténtalo de nuevo.'
            };
            setSaveError(stageMessages[currentStage] || 'No se pudieron guardar los cambios. Inténtalo de nuevo.');
        } finally {
            setSaving(false);
            setSaveStage(null);
        }
    };

    // Si hay borrados marcados, se confirma una sola vez por lote antes de tocar el backend
    // (open-questions.md B2=B) — el click en cada miniatura solo la había marcado, no borrado.
    const handleSaveClick = () => {
        if (totalPendingDeletes > 0) {
            setShowBatchDeleteConfirm(true);
            return;
        }
        performSave();
    };

    const handleConfirmBatchDelete = () => {
        setShowBatchDeleteConfirm(false);
        performSave();
    };

    // Campo imagen requerido que quedó en cero imágenes efectivas (persistidas - marcadas +
    // pendientes) — bloquea "Guardar Cambios" hasta volver a completar el campo (open-questions.md R1=B).
    // El cálculo es síncrono, no depende de ningún guardado previo.
    const incompleteRequiredImageField = (editForm.form?.fields || []).find(field =>
        field.required &&
        (field.type === 'image' || field.type === 'file') &&
        imageCounts[field.name] === 0
    );

    const saveStageLabel = {
        uploading: `Subiendo imágenes… ${Math.round(saveProgress)}%`,
        deleting: 'Eliminando imágenes…',
        'saving-form': 'Guardando cambios…'
    }[saveStage];

    return (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-9998">
            <div className="bg-surface rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden">
                <div className="shrink-0 px-6 pt-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        {mode === 'view' ? (
                            <Eye size={24} className="text-accent-text" />
                        ) : (
                            <PencilSimple size={24} className="text-warning" />
                        )}
                        <div>
                            <h3 className="text-xl font-bold text-content">
                                Respuesta de Formulario
                            </h3>
                            <p className="text-content-muted text-sm mt-1">
                                {editForm.form?.name} - {editForm.form?.type}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {user.role === 'admin' && <button
                            onClick={toggleMode}
                            className="px-4 py-2 bg-surface-2 hover:bg-line text-content rounded-lg text-sm font-medium transition-colors"
                        >
                            {mode === 'view' ? 'Cambiar a Edición' : 'Cambiar a modo Visualizar'}
                        </button>}
                        <button
                            onClick={handleCancel}
                            className="text-content-muted hover:text-content text-2xl font-bold"
                        >
                            ×
                        </button>
                    </div>
                </div>
                </div>

                <div ref={modalContentRef} className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
                <div className="space-y-4">
                    <div>
                            <NotificationAlert
                                origin="form-response-edit-modal"
                                notification={uploadNotification}
                                autoCloseMs={null}
                                onClose={() => setUploadNotification(null)}
                            />
                        </div>
                    {/* Información básica */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-content mb-1">
                                Técnico
                            </label>
                            <input
                                type="text"
                                value={editForm.technician?.name || ''}
                                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60"
                                disabled
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-content mb-1">
                                Cliente
                            </label>
                            <input
                                type="text"
                                value={editForm.task?.clientSnapshot?.name || ''}
                                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60"
                                disabled
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-content mb-1">
                                Tipo de Formulario
                            </label>
                            <input
                                type="text"
                                value={editForm.form?.type || ''}
                                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60"
                                disabled
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-content mb-1">
                                Estado
                            </label>
                            <select
                                value={editForm.status || ''}
                                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                disabled={mode === 'view'}
                                className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <option value="pendiente">Pendiente</option>
                                <option value="completado">Completado</option>
                                <option value="cancelado">Cancelado</option>
                            </select>
                        </div>
                    </div>

                    {/* Campos dinámicos del formulario */}
                    <div>
                        <label className="block text-sm font-medium text-content mb-2">
                            Datos del Formulario
                        </label>
                        <div className="bg-surface-2 p-4 rounded-lg border border-line">
                            {editForm.formData && typeof editForm.formData === 'object' ? (
                                Object.entries(editForm.formData).map(([key, value]) => (
                                    <div key={key} className="mb-4">
                                        <label className="block text-sm font-medium text-content mb-2">
                                            {key}
                                        </label>
                                        {renderFieldValue(key, value, editForm.form?.fields || [], editForm, mode === 'view')}
                                    </div>
                                ))
                            ) : (
                                <textarea
                                    value={editForm.formData || ''}
                                    onChange={(e) => setEditForm({ ...editForm, formData: e.target.value })}
                                    disabled={mode === 'view'}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60 disabled:cursor-not-allowed"
                                    placeholder="Datos del formulario..."
                                    maxLength={255}
                                />
                            )}
                        </div>
                    </div>

                    {/* Observaciones */}
                    <div>
                        <label className="block text-sm font-medium text-content mb-1">
                            Observaciones
                        </label>
                        <textarea
                            value={editForm.observations || ''}
                            onChange={(e) => setEditForm({ ...editForm, observations: e.target.value })}
                            disabled={mode === 'view'}
                            rows={3}
                            className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="Observaciones adicionales..."
                            maxLength={255}
                        />
                    </div>

                    {/* Botones de acción */}
                </div>
                </div>

                <div className="shrink-0 border-t border-line bg-surface px-6 py-4">
                    <div className="flex justify-between items-start">
                        {mode === 'edit' && onDelete && (
                            <button
                                onClick={handleDeleteClick}
                                className="bg-error hover:bg-error/80 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                            >

                                Eliminar
                            </button>
                        )}
                        <div className="flex flex-col items-end gap-2 ml-auto">
                            {mode === 'edit' && saveError && (
                                <p className="text-error text-xs text-right max-w-xs">
                                    {saveError}
                                </p>
                            )}
                            {mode === 'edit' && saving && saveStageLabel && (
                                <p className="text-accent-text text-xs text-right max-w-xs">
                                    {saveStageLabel}
                                </p>
                            )}
                            {mode === 'edit' && !saving && incompleteRequiredImageField && (
                                <p className="text-amber text-xs text-right max-w-xs">
                                    Completa "{incompleteRequiredImageField.label || incompleteRequiredImageField.name}" (requiere al menos una imagen) para poder guardar.
                                </p>
                            )}
                            <div className="flex space-x-3">
                                <button
                                    onClick={handleCancel}
                                    disabled={saving}
                                    className="bg-surface-2 hover:bg-line text-content px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancelar
                                </button>
                                {mode === 'edit' && (
                                    <button
                                        onClick={handleSaveClick}
                                        disabled={!!incompleteRequiredImageField || saving}
                                        className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber"
                                    >
                                        {saving ? 'Guardando…' : 'Guardar Cambios'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ConfirmDialog para eliminar la respuesta completa */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Confirmar eliminación"
                message="¿Estás seguro de que quieres eliminar esta respuesta de formulario? Esta acción no se puede deshacer."
                confirmText="Eliminar"
                cancelText="Cancelar"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />

            {/* ConfirmDialog para el lote de imágenes marcadas para eliminar (open-questions.md B2=B) */}
            <ConfirmDialog
                isOpen={showBatchDeleteConfirm}
                title="Eliminar imágenes"
                message={`Se eliminarán ${totalPendingDeletes} imagen${totalPendingDeletes === 1 ? '' : 'es'} de forma permanente al guardar. Esta acción no se puede deshacer.`}
                confirmText="Eliminar y guardar"
                cancelText="Cancelar"
                onConfirm={handleConfirmBatchDelete}
                onCancel={() => setShowBatchDeleteConfirm(false)}
            />
        </div>
    );
};

export default FormResponseEditModal;
